// @ts-strict-ignore
import urlSlugItemsEn from '@components/Localization/messages/urlSlugItems/en'
import urlSlugItemsNl from '@components/Localization/messages/urlSlugItems/nl'
import {
  FloatingServiceMenu,
  Footer,
  Header,
  Page,
  PageHero,
  PageTransition,
  pickSeoProps,
} from '@components/Page'
import { TourDetailFareHarborCTA } from '@components/_tours/detail/FareHarboCTA'
import Highlights from '@components/_tours/detail/Highlights'
import { Box, Checklist, Container, regular } from '@gassan-ui'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import {
  MultipleItemResponse,
  SingleItemResponse,
  fetchContent,
  getPageByUrlSlug,
} from '@lib/kontent'
import { TourPage as TourPageModel } from '@lib/kontent/models/TourPage'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import Script from 'next/script'
import styled from 'styled-components'

const Content = styled.div`
  ${regular};
  p {
    margin-bottom: 0.5rem;
  }
`

const TourPage: NextPage<SingleItemResponse<TourPageModel>> = (props) => {
  const listItems = props.item.elements.intro_check_list.value.split('\n').filter((t) => t !== '')
  const {
    i18n: { language },
  } = useTranslation('other')
  const { seo_metadata__alternate_url_slug } = props.item.elements
  return (
    <Page
      {...pickSeoProps(props.item.elements)}
      slug={`${language === 'nl' ? urlSlugItemsNl.toursPage : urlSlugItemsEn.toursPage}/${
        props.item.elements.url_slug.value
      }`}
      // If an alternate url slug is provided, we don't need to prepend it with the parent page url
      // If it's not provided, we prepend the url_slug with the parent page url
      altSlug={`${
        seo_metadata__alternate_url_slug.value !== ''
          ? seo_metadata__alternate_url_slug.value
          : `${language === 'nl' ? urlSlugItemsEn.toursPage : urlSlugItemsNl.toursPage}/${
              props.item.elements.url_slug.value
            }`
      }`}
    >
      <Script
        type="text/javascript"
        src="https://fareharbor.com/embeds/api/v1/?autolightframe=yes"
        strategy="afterInteractive"
      />
      <FloatingServiceMenu />
      <Header initialVariant="absolute" />
      <PageTransition>
        {props.item.elements.page_hero__mobile_image.value[0] &&
          props.item.elements.page_hero__desktop_image.value[0] && (
            <PageHero
              title={props.item.elements.title.value}
              description={props.item.elements.page_hero__description.value}
              mobileSrc={props.item.elements.page_hero__mobile_image.value[0].url}
              desktopSrc={props.item.elements.page_hero__desktop_image.value[0].url}
              alt={props.item.elements.page_hero__desktop_image.value[0].description}
              textAlign={
                props.item.elements?.page_hero__align_text?.value[0]?.codename === 'left'
                  ? 'left'
                  : 'center'
              }
            />
          )}
        <Container>
          <Box width={{ _: '100%', large: '90%' }} mx="auto">
            <Box
              display="grid"
              gridTemplateColumns={{ _: '100%', medium: 'repeat(2, 1fr)' }}
              gridColumnGap={{ medium: '4rem', large: '7rem' }}
              pt={{ _: '2.5rem', medium: '3rem', large: '5.75rem' }}
              mb={{ _: '2.5rem', medium: '3rem' }}
            >
              <Content
                dangerouslySetInnerHTML={{ __html: props.item.elements.intro_text.value }}
              ></Content>
              <Checklist>
                {listItems.map((content, i) => (
                  <li key={i}>{content}</li>
                ))}
              </Checklist>
            </Box>
            <TourDetailFareHarborCTA
              ctaUrl={props.item.elements.booking_button_url.value}
              tourTitle={props.item.elements.title.value}
              codename={props.item.system.codename}
            />
            <Highlights {...props} />
            <TourDetailFareHarborCTA
              ctaUrl={props.item.elements.booking_button_url.value}
              tourTitle={props.item.elements.title.value}
              codename={props.item.system.codename}
            />
          </Box>
        </Container>
        <Footer />
      </PageTransition>
    </Page>
  )
}

export default TourPage

export const getStaticProps: GetStaticProps = async ({ locale, params, preview }) => {
  const urlSlug = typeof params.slug === 'string' ? params.slug : params.slug.join('/')
  const content = await getPageByUrlSlug<TourPageModel>({
    language: locale,
    urlSlug,
    preview,
  })
  return {
    props: {
      ...content,
      ...(await getServerSideTranslations(locale)),
    },
  }
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  let paths = []
  for (let locale of locales) {
    // Get store detail pages
    const storePages: MultipleItemResponse<TourPageModel> = await fetchContent({
      path: '',
      params: {
        elements: ['url_slug'],
        'system.type[in]': ['tour_page'],
        language: locale,
      },
    })

    storePages.items.forEach((item) => {
      if (item.system.language === locale) {
        paths.push({
          params: {
            slug: [item.elements.url_slug.value],
          },
          locale,
        })
      }
    })
  }

  return { paths, fallback: false }
}
